/**
 * Persist NCCN review feedback and await order-level recompute.
 *
 * `review_submitted` is not flipped to true until recompute has written
 * `ai_review_refuted`. Otherwise the worklist Human Validation CASE treats
 * AI Met/Not-Met + `ai_review_refuted = false` as Agreed.
 * @module persistNccnReview
 */
import {
  recomputeNccnEvaluation,
  submitNccnEvaluation,
} from "../../../api/postCall/nccnEvaluation";
import { NCCNEvaluationPayload } from "../../../data-model/nccnEvaluation";
import { EvaluationResult } from "../../../data-model/nccnGuidelines";
import {
  applyHumanVerdictToFinalResult,
  resolveReviewSubmitted,
} from "./nccnReviewFeedback";

/** Arguments for {@link persistNccnReview}. */
export interface PersistNccnReviewArgs {
  /** Order the evaluation belongs to. */
  orderId: string;
  /** Patient MRN, when known. */
  patientId?: string | null;
  /** Organisation ID for RLS-scoped writes, when known. */
  orgId?: string | null;
  /** Submitting user ID. */
  userId: string;
  /** Draft to persist (guideline reviews, criteria feedback). */
  nextDraft: NCCNEvaluationPayload;
  /** Envelope/local `review_submitted` from a previous true write. */
  alreadySubmitted: boolean;
  /** True on guideline-verdict commits; triggers order-level recompute. */
  shouldRecompute: boolean;
  /**
   * Treat this write as a completed review regardless of mandatory progress.
   * Set by the terminal Review / Done submit, whose only precondition is that
   * the reviewer finished the flow the panel offered them. That flow can have
   * zero mandatory guidelines (the AI marked every guideline Not Met), in
   * which case {@link resolveReviewSubmitted} never reports complete and the
   * row would stay Yet To Validate forever.
   */
  forceSubmitted?: boolean;
  /** Current order-level result from envelope; preserved if no recompute. */
  currentOrderResult?: EvaluationResult | null;
  /** Current `ai_review_refuted` from envelope; preserved if no recompute. */
  currentAiReviewRefuted?: boolean;
}

/**
 * Thrown when guideline feedback was stored but order-level recompute
 * (or the follow-up `review_submitted` write) failed. The row stays Yet To
 * Validate so Human Validation cannot mislabel it as Agreed.
 */
export class NccnRecomputeError extends Error {
  /** Underlying submit/recompute failure, when present. */
  readonly underlyingError: unknown;

  /**
   * @param message - Safe-to-display failure summary
   * @param cause - Underlying submit/recompute error
   */
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NccnRecomputeError";
    this.underlyingError = cause;
  }
}

/** Result of a successful {@link persistNccnReview}. */
export interface PersistNccnReviewResult {
  /** Payload last written by submit (includes the final `review_submitted`). */
  payload: NCCNEvaluationPayload;
  /**
   * `review_submitted` as actually persisted by this call — false while the
   * flag is still deferred, so caller state never claims a flag the row lacks.
   */
  reviewSubmitted: boolean;
  /** Order-level result after recompute, or the draft value when skipped. */
  orderEvaluationResult?: EvaluationResult | null;
  /** Whether a human recompute changed the AI order-level result. */
  aiReviewRefuted: boolean;
}

/**
 * Submits the evaluation JSON, then (when requested) awaits recompute
 * before flipping `review_submitted` to true.
 *
 * Completion is derived from mandatory progress unless `forceSubmitted` is
 * set, which the terminal submit uses so an order with no mandatory
 * guidelines can still be marked reviewed.
 *
 * A first-time completion (`submitted && !alreadySubmitted`) is written with
 * `review_submitted: false` so the worklist stays Yet To Validate. After
 * recompute lands `ai_review_refuted`, a second submit sets `review_submitted:
 * true`. If recompute fails, the flag stays false and this throws.
 *
 * The deferral does not depend on `shouldRecompute`: a criteria-step save of an
 * already-complete draft (e.g. after a failed summary submit, or on a row whose
 * `review_submitted` column was never backfilled) must not flip the flag
 * either, since no recompute runs to write `ai_review_refuted`. Such a save
 * keeps the flag false and the next guideline-verdict commit stamps it.
 *
 * When `shouldRecompute` is false (non-summary-step save), the frozen JSONB
 * payload is ignored in favor of the caller's envelope state. This prevents
 * criteria-step saves from overwriting a summary-step human override with
 * stale AI values.
 *
 * @param args - Order identifiers, draft, and whether to recompute
 * @returns The last-written payload and envelope flags
 * @throws {Error} When the first submit fails
 * @throws {NccnRecomputeError} When recompute or the follow-up submit fails
 *   after feedback was already stored
 */
export const persistNccnReview = async ({
  orderId,
  patientId,
  orgId,
  userId,
  nextDraft,
  alreadySubmitted,
  shouldRecompute,
  currentOrderResult,
  currentAiReviewRefuted,
  forceSubmitted = false,
}: PersistNccnReviewArgs): Promise<PersistNccnReviewResult> => {
  const submitted =
    forceSubmitted || resolveReviewSubmitted(nextDraft, alreadySubmitted);
  const deferSubmitted = submitted && !alreadySubmitted;
  const writtenSubmitted = deferSubmitted ? false : submitted;
  const payload: NCCNEvaluationPayload = {
    ...nextDraft,
    review_submitted: writtenSubmitted,
  };

  await submitNccnEvaluation({
    order_id: orderId,
    patient_id: patientId ?? null,
    org_id: orgId ?? null,
    user_id: userId,
    response_json: payload,
  });

  // Preserve envelope state when no recompute is requested; otherwise,
  // initialize from the frozen JSONB payload (which will be overwritten
  // below if recompute actually runs).
  let orderEvaluationResult = shouldRecompute
    ? payload.order_evaluation_result
    : currentOrderResult;
  let aiReviewRefuted = shouldRecompute
    ? Boolean(payload.ai_review_refuted)
    : (currentAiReviewRefuted ?? false);

  if (shouldRecompute) {
    try {
      const recomputed = await recomputeNccnEvaluation({
        order_id: orderId,
        org_id: orgId ?? null,
        response_json: applyHumanVerdictToFinalResult({
          ...payload,
          review_submitted: submitted,
        }),
      });
      orderEvaluationResult = recomputed.order_evaluation_result;
      aiReviewRefuted = Boolean(recomputed.ai_review_refuted);
    } catch (error) {
      throw new NccnRecomputeError("NCCN order-level recompute failed", error);
    }

    if (deferSubmitted) {
      const submittedPayload: NCCNEvaluationPayload = {
        ...payload,
        review_submitted: true,
      };
      try {
        await submitNccnEvaluation({
          order_id: orderId,
          patient_id: patientId ?? null,
          org_id: orgId ?? null,
          user_id: userId,
          response_json: submittedPayload,
        });
      } catch (error) {
        throw new NccnRecomputeError(
          "NCCN review_submitted write failed after recompute",
          error,
        );
      }
      return {
        payload: submittedPayload,
        reviewSubmitted: true,
        orderEvaluationResult,
        aiReviewRefuted,
      };
    }
  }

  return {
    payload,
    reviewSubmitted: writtenSubmitted,
    orderEvaluationResult,
    aiReviewRefuted,
  };
};
